import { useState } from 'react';
import { format, formatDistanceToNow } from 'date-fns';
import { 
  MoreVertical, 
  Pin, 
  PinOff, 
  Edit, 
  Trash2, 
  Lock, 
  AlertTriangle,
  Info,
  Star,
  Clock,
  Bell
} from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Note, NoteType, getNoteTypeColor, getNoteTypeLabel } from '@/app/types/note';
import { useAuthStore } from '@/app/store/authStore';
import { cn } from '@/lib/utils';

interface NoteCardProps {
  note: Note;
  onEdit?: (note: Note) => void;
  onDelete?: (noteId: number) => void;
  onTogglePin?: (noteId: number) => void;
  compact?: boolean;
  showActions?: boolean;
}

const noteTypeIcons = {
  [NoteType.GENERAL]: Info,
  [NoteType.WARNING]: AlertTriangle,
  [NoteType.IMPORTANT]: Star,
  [NoteType.FOLLOW_UP]: Clock,
  [NoteType.REMINDER]: Bell,
  [NoteType.INTERNAL]: Lock,
};

export default function NoteCard({
  note,
  onEdit,
  onDelete,
  onTogglePin,
  compact = false,
  showActions = true
}: NoteCardProps) {
  const user = useAuthStore((state) => state.user);
  const [isExpanded, setIsExpanded] = useState(!compact);
  
  const isCreator = user && note.created_by === parseInt(user.id);
  const canEdit = isCreator && showActions;
  
  const Icon = noteTypeIcons[note.note_type] || Info;

  const formatTimestamp = (date: string) => {
    const noteDate = new Date(date);
    const now = new Date();
    const diffInHours = (now.getTime() - noteDate.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return formatDistanceToNow(noteDate, { addSuffix: true });
    } else {
      return format(noteDate, 'MMM d, yyyy h:mm a');
    }
  };

  const handleToggleExpand = () => {
    if (compact) {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <Card className={cn(
      'transition-all duration-200',
      note.is_pinned && 'border-info/30 bg-info/5/50',
      compact && !isExpanded && 'cursor-pointer hover:shadow-md'
    )}>
      <CardHeader className="pb-3" onClick={handleToggleExpand}>
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-3 flex-1">
            {/* Note Type Icon and Badge */}
            <div className="flex items-center gap-2">
              <div className={cn(
                'p-1.5 rounded',
                getNoteTypeColor(note.note_type).split(' ')[1]
              )}>
                <Icon className={cn(
                  'h-4 w-4',
                  getNoteTypeColor(note.note_type).split(' ')[0]
                )} />
              </div>
              {note.is_pinned && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Pin className="h-4 w-4 text-info" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Pinned note</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>

            {/* Title and Metadata */}
            <div className="flex-1 min-w-0">
              {note.title && (
                <h4 className="font-medium text-sm mb-1 truncate">
                  {note.title}
                </h4>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className={cn(
                  'text-xs',
                  getNoteTypeColor(note.note_type)
                )}>
                  {getNoteTypeLabel(note.note_type)}
                </Badge>
                {note.is_private && (
                  <Badge variant="secondary" className="text-xs">
                    <Lock className="h-3 w-3 mr-1" />
                    Private
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">
                  by {note.created_by_user?.full_name || note.created_by_user?.username || 'Unknown'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatTimestamp(note.created_at)}
                </span>
                {note.updated_at && (
                  <span className="text-xs text-muted-foreground italic">
                    (edited)
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Actions Menu */}
          {canEdit && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="sr-only">Open menu</span>
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onTogglePin && (
                  <DropdownMenuItem onClick={() => onTogglePin(note.id)}>
                    {note.is_pinned ? (
                      <>
                        <PinOff className="mr-2 h-4 w-4" />
                        Unpin
                      </>
                    ) : (
                      <>
                        <Pin className="mr-2 h-4 w-4" />
                        Pin to top
                      </>
                    )}
                  </DropdownMenuItem>
                )}
                {onEdit && (
                  <DropdownMenuItem onClick={() => onEdit(note)}>
                    <Edit className="mr-2 h-4 w-4" />
                    Edit
                  </DropdownMenuItem>
                )}
                {onDelete && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      onClick={() => onDelete(note.id)}
                      className="text-destructive focus:text-destructive"
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </CardHeader>

      {(!compact || isExpanded) && (
        <CardContent>
          <div className="prose prose-sm max-w-none">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">
              {note.content}
            </p>
          </div>

          {/* Extra data tags if present */}
          {note.extra_data?.tags && note.extra_data.tags.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {note.extra_data.tags.map((tag: string, index: number) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
            </div>
          )}

          {/* Priority indicator if present */}
          {note.extra_data?.priority && (
            <div className="mt-2">
              <Badge 
                variant={
                  note.extra_data.priority === 'critical' ? 'destructive' :
                  note.extra_data.priority === 'high' ? 'default' :
                  'secondary'
                }
                className="text-xs"
              >
                Priority: {note.extra_data.priority}
              </Badge>
            </div>
          )}

          {/* Due date if present */}
          {note.extra_data?.due_date && (
            <div className="mt-2 text-xs text-muted-foreground">
              Due: {format(new Date(note.extra_data.due_date), 'MMM d, yyyy')}
            </div>
          )}
        </CardContent>
      )}

      {compact && !isExpanded && note.content.length > 100 && (
        <CardContent className="pt-0">
          <p className="text-xs text-muted-foreground italic">
            Click to expand...
          </p>
        </CardContent>
      )}
    </Card>
  );
}